// noinspection SpellCheckingInspection

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from '@mui/material/InputAdornment';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { useAttorneyHubDispatch } from 'app/realtime-store/redux-store';
import { OrderDirection } from 'common/contracts/order-direction';
import PaginationParameters from 'common/contracts/pagination-parameters';
import { addSpacesToTitleCase } from 'common/helpers/utils';
import { DocumentQuery } from 'documents/entities/document/api/document-api-service';
import DocumentParameters, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters
} from 'documents/entities/document/api/request-contracts/document-parameters';
import Document from 'documents/entities/document/document';
import {
  getDocumentsByQuery,
  getDocumentsQueryByInterface,
  getIsLoadingDocumentsByQuery,
  populateDocumentsByQuery,
  updateDocumentsInterfaceQuery
} from 'documents/entities/document/store/documents-redux-slice';
import UnavailableDocument from 'documents/values/unavailable-document';
import EntityAgreement from 'legal-entities/entities/entity-agreement/entity-agreement';
import {
  getEntityAgreementsByIds,
  getIsLoadingEntityAgreementByIds,
  populateEntityAgreements
} from 'legal-entities/entities/entity-agreement/store/entity-agreements-redux-slice';
import QueryForumsApiRequest, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters
} from 'messaging/entities/forum/api/request-contracts/query-forums-api-request';
import Forum from 'messaging/entities/forum/forum';
import {
  getForumQueryByInterface,
  getForumsByQuery,
  getIsLoadingForumsByQuery,
  populateForumsByQuery,
  updateForumInterfaceQuery
} from 'messaging/entities/forum/store/forums-redux-slice';
import { ChildResourceType } from 'permissions/entities/permission/resources/general';
import React, { useEffect } from "react";
import { useSession } from 'users/session/session-context';
import ProposalParameters, {
  ProposalBoxType, ProposalContext,
  ProposalFilterParameters,
  ProposalOrderField,
  ProposalOrderParameters
} from 'work/entities/proposal/api/request-contracts/proposal-parameters';
import Proposal from 'work/entities/proposal/proposal';
import {
  getIsLoadingProposalsByQuery,
  getProposalQueryByInterface,
  getProposalsByQuery,
  populateProposalsByQuery,
  ProposalQuery,
  updateProposalInterfaceQuery
} from 'work/entities/proposal/store/proposals-redux-slice';
import WorkAgreementParameters, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderField,
  WorkAgreementOrderParameters
} from 'work/entities/work-agreement/api/request-contracts/work-agreement-parameters';
import {
  getIsLoadingWorkAgreementsByQuery,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  populateWorkAgreementsByQuery,
  updateWorkAgreementInterfaceQuery,
  WorkAgreementQuery
} from 'work/entities/work-agreement/store/work-agreement-redux-slice';
import WorkAgreement from 'work/entities/work-agreement/work-agreement';

// Styles
const SplitSearchContainer = styled('div')(
  () => ({
    width: '100%'
  }));
const ControlContainer = styled('div')(
  () => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
  }));
const TargetFormControl = styled(FormControl)(
  () => ({
    flex: 1,
    width: '100%'
  }));
const CategoryDropdown = styled(Button)(({ theme }) => ({
  borderBottomRightRadius: 0,
  borderTopRightRadius: 0,
  fontSize: '0.8em',
  padding: `0 ${theme.spacing(2.5)}`
}));
const SearchTextField = styled(TextField)(({ theme }) => ({
  boxSizing: 'border-box',
  minWidth: '275px',
  width: '100%',
  '& .MuiInputBase-root': {
    borderRadius: '0 4px 4px 0',
    '& fieldset': {
      borderColor: '#777',
      borderLeftWidth: 0,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    }
  }
}));

// Types
export type Kind = Extract<keyof typeof ChildResourceType, string>;
export type SearchOption = {
  kind: Kind;
  id: string;
  label: string;
  secondary?: string;
};
type SplitSearchProps = {
  className?: string;
  disabled?: boolean;
  onTargetSelected: (target: SearchOption | null) => void;
};

export default function SplitSearch(props: Readonly<SplitSearchProps>) {
  // Props
  const {
    className,
    disabled,
    onTargetSelected
  } = props;

  // Refs
  const menuAnchorRef = React.useRef<HTMLButtonElement>(null);

  // State
  const [selectedCategory, setSelectedCategory] = React.useState<Kind | null>(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [target, setTarget] = React.useState<SearchOption | null>(null);
  const [documents, setDocuments] = React.useState<(Document | UnavailableDocument)[]>([]);
  const [proposals, setProposals] = React.useState<Proposal[]>([]);
  const [workAgreements, setWorkAgreements] = React.useState<WorkAgreement[]>([]);
  const [forums, setForums] = React.useState<Forum[]>([]);
  const [entityAgreements, setEntityAgreements] = React.useState<EntityAgreement[]>([]);

  // Debounced Search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  // Helper to get only the string keys of an enum
  // Useful since typescript enums have both string and numeric keys
  // See: https://www.typescriptlang.org/docs/handbook/enums.html#reverse-mappings
  function enumStringKeys<T extends object>(e: T): Array<Extract<keyof T, string>> {
    return Object.keys(e).filter(k => Number.isNaN(Number(k))) as Array<Extract<keyof T, string>>;
  }

  // Helper to convert Kind to a human-readable label
  // e.g. ProposalAccess -> Proposal
  // e.g. WorkAgreementAccess -> Work Agreement
  const kindToLabel = (k: Kind) => addSpacesToTitleCase(k.replace('Access', ''));

  // Memoized Categories
  const categories = React.useMemo<Kind[]>(() => enumStringKeys(ChildResourceType), []);

  // Documents Query
  const documentsInterfaceName = "10RecentContracts";
  const documentQuery = getDocumentsQueryByInterface(documentsInterfaceName);
  const documentsResponse = getDocumentsByQuery(documentQuery);
  const documentsLoading = getIsLoadingDocumentsByQuery(documentQuery);

  // Proposals Query
  const proposalsInterfaceName = "10RecentProposals";
  const proposalQuery = getProposalQueryByInterface(proposalsInterfaceName);
  const proposalsResponse = getProposalsByQuery(proposalQuery);
  const proposalsLoading = getIsLoadingProposalsByQuery(proposalQuery);

  // Work Agreements Query
  const workAgreementsInterfaceName = "10RecentWorkAgreements";
  const workAgreementQuery = getWorkAgreementQueryByInterface(workAgreementsInterfaceName);
  const workAgreementsResponse = getWorkAgreementsByQuery(workAgreementQuery);
  const workAgreementsLoading = getIsLoadingWorkAgreementsByQuery(workAgreementQuery);

  // Forums Query
  const forumsInterfaceName = "10RecentForums";
  const forumQuery = getForumQueryByInterface(forumsInterfaceName);
  const forumsResponse = getForumsByQuery(forumQuery);
  const forumsLoading = getIsLoadingForumsByQuery(forumQuery);

  // Entity Agreements Query
  const entityAgreementIds = session.openEntityAgreements.map(entityAgreement => entityAgreement.id);
  const entityAgreementsResponse = getEntityAgreementsByIds(entityAgreementIds);
  const entityAgreementsLoading = getIsLoadingEntityAgreementByIds(entityAgreementIds);

  // Loading State
  const isLoading: boolean = (
    documentsLoading ||
    proposalsLoading ||
    workAgreementsLoading ||
    forumsLoading ||
    entityAgreementsLoading
  );

  // Lifecycle
  useEffect(
    () => {
      if (selectedCategory !== 'EntityAgreementAccess') return;
      if (entityAgreementIds.length === 0) {
        setEntityAgreements([]);
        return;
      }
      if (entityAgreementsResponse === undefined && !entityAgreementsLoading) {
        dispatch(populateEntityAgreements({
          session: session,
          entityAgreementIds: entityAgreementIds
        }));
      } else if (entityAgreementsResponse !== undefined) {
        setEntityAgreements(entityAgreementsResponse);
      }
    },
    [
      selectedCategory,
      entityAgreementsResponse,
      entityAgreementIds,
      entityAgreementsLoading,
      dispatch,
      session
    ]
  );

  // Switch between "recent 10" and "search mode"
  useEffect(() => {
    if (!selectedCategory) return;

    const isSearching = debouncedSearchTerm.length > 0;

    switch (selectedCategory) {
      case 'DocumentAccess': {
        if (selectedCategory !== 'DocumentAccess') break;
        const query = new DocumentQuery(
          new DocumentParameters(
            new DocumentOrderParameters(
              DocumentOrderField.Created, 
              OrderDirection.Descending
            ),
            new DocumentFilterParameters({
              query: isSearching ? debouncedSearchTerm : undefined
            }),
          ),
          new PaginationParameters(0, 10)
        );

        if (!documentQuery) {
          dispatch(updateDocumentsInterfaceQuery({
            interfaceName: isSearching ? 'SearchDocuments' : '10RecentContracts',
            query
          }));
          break;
        }

        if (documentQuery && documentsResponse === undefined && !documentsLoading) {
          dispatch(populateDocumentsByQuery({ session, query }));
        }
        else if (documentsResponse !== undefined) {
          setDocuments(documentsResponse.data);
        }
        break;
      }

      case 'ProposalAccess': {
        if (!session.user?.companyEntityId || selectedCategory !== 'ProposalAccess') break;
        const query = new ProposalQuery(
          new ProposalParameters(
            new ProposalFilterParameters({
              context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
              boxType: ProposalBoxType.AllActive,
            }),
            new ProposalOrderParameters(
              ProposalOrderField.Created,
              OrderDirection.Descending
            )
          ),
          new PaginationParameters(0, 10)
        );

        if (!proposalQuery) {
          dispatch(updateProposalInterfaceQuery({
            interfaceName: isSearching ? 'SearchProposals' : '10RecentProposals',
            query
          }));
          break;
        }

        if (proposalQuery && proposalsResponse === undefined && !proposalsLoading) {
          dispatch(populateProposalsByQuery({ session, query }));
        }
        else if (proposalsResponse !== undefined) {
          setProposals(proposalsResponse.data);
        }
        break;
      }

      case 'WorkAgreementAccess': {
        if (!session.user?.companyEntityId || selectedCategory !== 'WorkAgreementAccess') break;
        const query = new WorkAgreementQuery(
          new WorkAgreementParameters(
            new WorkAgreementFilterParameters(),
            new WorkAgreementOrderParameters(
              WorkAgreementOrderField.Start, 
              OrderDirection.Descending
            )
          ),
          new PaginationParameters(0, 10)
        );

        if (!workAgreementQuery) {
          dispatch(updateWorkAgreementInterfaceQuery({
            interfaceName: isSearching ? 'SearchWorkAgreements' : '10RecentWorkAgreements',
            query
          }));
          break;
        }

        if (workAgreementQuery && workAgreementsResponse === undefined && !workAgreementsLoading) {
          dispatch(populateWorkAgreementsByQuery({ session, query }));
        }
        else if (workAgreementsResponse !== undefined)
        {
          setWorkAgreements(workAgreementsResponse.data);
        }
        break;
      }

      case 'ForumAccess': {
        if (selectedCategory !== 'ForumAccess') return;
        const query = new QueryForumsApiRequest(
          new ForumFilterParameters(),
          new ForumOrderParameters(
            ForumOrderField.CreatedDate, 
            OrderDirection.Descending
          ),
          new PaginationParameters(0, 10),
          isSearching ? debouncedSearchTerm : undefined
        );

        if (!forumQuery) {
          dispatch(updateForumInterfaceQuery({
            interfaceName: isSearching ? 'SearchForums' : '10RecentForums',
            query
          }));
          break;
        }

        if (forumQuery && forumsResponse === undefined && !forumsLoading) {
          dispatch(populateForumsByQuery({ session, query }));
        }
        else if (forumsResponse !== undefined) {
          setForums(forumsResponse);
        }
        break;
      }
    }
  }, [
    selectedCategory,
    documentsResponse,
    documentQuery,
    documentsLoading,
    dispatch,
    session
  ]);

  // Option Converters
  // Convert each entity type to a SearchOption to be used in the Autocomplete component
  const toDocumentOption = (d: Document | UnavailableDocument): SearchOption => ({
    kind: 'DocumentAccess',
    id: String(d.id),
    label: 'name' in d && d.name ? d.name.toString() : 'Unavailable Document',
  });

  const toProposalOption = (p: Proposal): SearchOption => ({
    kind: 'ProposalAccess',
    id: String(p.id),
    label: p.name?.value ?? `Proposal ${p.id}`,
  });

  const toWorkAgreementOption = (w: WorkAgreement): SearchOption => ({
    kind: 'WorkAgreementAccess',
    id: String(w.id),
    label: w.name?.value ?? `Work Agreement ${w.id}`,
  });

  const toForumOption = (f: Forum): SearchOption => ({
    kind: 'ForumAccess',
    id: String(f.id),
    label: f.name ?? `Forum ${f.id}`,
  });

  const toEntityAgreementOption = (e: EntityAgreement): SearchOption => ({
    kind: 'EntityAgreementAccess',
    id: String(e.id),
    label: e.entityName.value ?? `Entity Agreement ${e.id}`,
  });

  // All possible options
  const allOptions: SearchOption[] = React.useMemo(() => [
    ...(documents ?? []).map(toDocumentOption),
    ...(proposals ?? []).map(toProposalOption),
    ...(forums ?? []).map(toForumOption),
    ...(workAgreements ?? []).map(toWorkAgreementOption),
    ...(entityAgreements ?? []).map(toEntityAgreementOption)
  ], [
    documents,
    proposals,
    workAgreements,
    forums,
    entityAgreements
  ]);

  // Filtered Options based on selected category
  const filteredOptions = React.useMemo(
    () => (selectedCategory ? allOptions.filter(o => o.kind === selectedCategory) : []),
    [allOptions, selectedCategory]
  );

  // Handlers

  /**
   * Handle category selection from the dropdown menu
   * @param _event The mouse event
   * @param category The selected category
   */
  function handleCategorySelected(_event: React.MouseEvent, category: Kind) {
    setSelectedCategory(category);
    setCategoryMenuOpen(false);
    setTarget(null);
    setSearchTerm('');
  }

  return (
    <SplitSearchContainer className={className}>
      <TargetFormControl>
        <FormControlLabel
          control={
            <ControlContainer>
              <CategoryDropdown
                ref={menuAnchorRef}
                disabled={disabled || isLoading}
                disableElevation
                variant="contained"
                endIcon={<ArrowDropDownIcon />}
                onClick={() => setCategoryMenuOpen(!categoryMenuOpen)}>
                {selectedCategory ? kindToLabel(selectedCategory) : 'Select'}
              </CategoryDropdown>
              <Menu
                keepMounted
                open={categoryMenuOpen}
                onClose={() => setCategoryMenuOpen(false)}
                anchorEl={menuAnchorRef.current}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {categories.map(category => (
                  <MenuItem
                    key={category}
                    onClick={(event) => handleCategorySelected(event, category)}>
                    {kindToLabel(category)}
                  </MenuItem>
                ))}
              </Menu>

              <Autocomplete<SearchOption, false, false, false>
                sx={{ width: '100%' }}
                options={filteredOptions}
                loading={isLoading}
                loadingText="Loading..."
                disabled={disabled || !selectedCategory || isLoading}
                isOptionEqualToValue={(a, b) => a.id === b.id && a.kind === b.kind}
                getOptionLabel={(opt) => opt.label}
                filterOptions={(opts) => opts}
                inputValue={searchTerm}
                onInputChange={(_e, newInput) => setSearchTerm(newInput)}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.kind}:${option.id}`}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{option.label}</span>
                      {option.secondary && (
                        <small style={{ opacity: 0.7 }}>{option.secondary}</small>
                      )}
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <SearchTextField
                    {...params}
                    placeholder={
                      selectedCategory
                        ? `Search ${kindToLabel(selectedCategory)}`
                        : 'Select category to begin'
                    }
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><SearchIcon /></InputAdornment>
                            {params.InputProps?.startAdornment}
                          </>
                        ),
                      },
                      htmlInput: { ...params.inputProps },
                    }}
                  />
                )}
                slotProps={{
                  popper: { sx: { zIndex: (t) => t.zIndex.modal + 1 } },
                }}
                value={
                  target
                    ? filteredOptions.find(o => o.id === target.id && o.kind === target.kind) ?? null
                    : null
                }
                onChange={(_event, value) => {
                  setTarget(value);
                  onTargetSelected(value);
                }}
              />
            </ControlContainer>
          }
          label='Target'
          labelPlacement="top"
          sx={{
            alignItems: 'stretch',
            margin: 0
          }}
          slotProps={{
            typography: {
              fontWeight: '500',
              marginBottom: 1,
              marginRight: 'auto'
            }
          }}
        />
      </TargetFormControl>
    </SplitSearchContainer>
  );
}
